import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, setBearer } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LockKey, Check, X } from "@phosphor-icons/react";

// Frontend password-strength mirror. Backend re-validates every rule; this is
// purely UX. Never trust these constants for authorization.
const RULES = [
  { key: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "upper", label: "One uppercase letter (A–Z)", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter (a–z)", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "One digit (0–9)", test: (p) => /[0-9]/.test(p) },
  { key: "symbol", label: "One symbol (! @ # $ etc.)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function ChangePassword() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const ruleState = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(next) })), [next]);
  const allRulesOk = ruleState.every((r) => r.ok);
  const confirmOk = confirm.length > 0 && confirm === next;
  const differsFromCurrent = next.length > 0 && next !== current;
  const readyToSubmit = current.length > 0 && allRulesOk && confirmOk && differsFromCurrent;

  const submit = async (e) => {
    e.preventDefault();
    if (!readyToSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      // Backend has cleared cookies + incremented password_version. Clear any
      // client-side bearer, force logout, and redirect to /login so the user
      // must sign in again with the new password (per production requirement).
      setBearer(null);
      toast.success("Password changed. Please sign in again with your new password.");
      try { await logout(); } catch { /* logout is best-effort */ }
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const forced = !!user?.must_change_password;

  return (
    <div className="p-4 md:p-6 max-w-xl">
      <div className="mb-4">
        <div className="label-tech mb-1">Account Security</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Change Password</h1>
        {forced && (
          <p className="mt-3 text-sm border border-primary/50 bg-primary/10 text-foreground px-3 py-2 rounded-sm" data-testid="cp-forced-banner">
            <LockKey size={14} weight="bold" className="inline mr-1 -mt-0.5" />
            Password change is required before you can continue.
          </p>
        )}
      </div>

      <form onSubmit={submit} className="card-flat border-border p-5 space-y-4">
        <label className="block">
          <div className="label-tech mb-1">Current Password</div>
          <input
            data-testid="cp-current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="w-full h-11 px-3 border border-border bg-background rounded-sm font-mono text-sm"
            required
          />
        </label>

        <label className="block">
          <div className="label-tech mb-1">New Password</div>
          <input
            data-testid="cp-new"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="w-full h-11 px-3 border border-border bg-background rounded-sm font-mono text-sm"
            required
          />
        </label>

        <label className="block">
          <div className="label-tech mb-1">Confirm New Password</div>
          <input
            data-testid="cp-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full h-11 px-3 border border-border bg-background rounded-sm font-mono text-sm"
            required
          />
          {confirm.length > 0 && !confirmOk && (
            <div className="text-xs text-destructive mt-1 font-mono" data-testid="cp-confirm-error">Passwords do not match</div>
          )}
        </label>

        <div className="border border-border rounded-sm p-3 bg-muted/40" data-testid="cp-rules">
          <div className="label-tech mb-2">Password Requirements</div>
          <ul className="space-y-1 text-xs font-mono">
            {ruleState.map((r) => (
              <li key={r.key} className="flex items-center gap-2">
                {r.ok
                  ? <Check size={12} weight="bold" className="text-success" />
                  : <X size={12} weight="bold" className="text-muted-foreground" />}
                <span className={r.ok ? "text-success" : "text-muted-foreground"}>{r.label}</span>
              </li>
            ))}
            <li className="flex items-center gap-2">
              {differsFromCurrent
                ? <Check size={12} weight="bold" className="text-success" />
                : <X size={12} weight="bold" className="text-muted-foreground" />}
              <span className={differsFromCurrent ? "text-success" : "text-muted-foreground"}>Must differ from current password</span>
            </li>
          </ul>
        </div>

        <button
          data-testid="cp-submit"
          type="submit"
          disabled={!readyToSubmit || busy}
          className="btn-hard w-full h-11 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white disabled:opacity-50"
        >
          {busy ? "Changing…" : "Change Password"}
        </button>
      </form>
    </div>
  );
}
